import { useState, useEffect } from "react";
import { Button, useDisclosure } from "@heroui/react";
import { Plus, Eye } from "@phosphor-icons/react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { useSession } from "@clerk/clerk-react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

// Import components and types
import {
  CreateTemplateModal,
  EditTemplateModal,
  PreviewTemplateModal,
  DeleteTemplateModal,
  CreateTypeModal,
  TemplateTypesSection,
  TemplatesTable,
  EmailSettingsSection,
  EmailTemplate,
  TemplateType,
  EmailSettings
} from "./components";

const MailTemplatesTab = () => {
  const { session } = useSession();
  const navigate = useNavigate();

  // State
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [templateTypes, setTemplateTypes] = useState<TemplateType[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null);
  const [emailSettings, setEmailSettings] = useState<EmailSettings | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Modal controls
  const { isOpen: isCreateModalOpen, onOpen: onCreateModalOpen, onClose: onCreateModalClose } = useDisclosure();
  const { isOpen: isEditModalOpen, onOpen: onEditModalOpen, onClose: onEditModalClose } = useDisclosure();
  const { isOpen: isPreviewModalOpen, onOpen: onPreviewModalOpen, onClose: onPreviewModalClose } = useDisclosure();
  const { isOpen: isDeleteModalOpen, onOpen: onDeleteModalOpen, onClose: onDeleteModalClose } = useDisclosure();
  const { isOpen: isTypeModalOpen, onOpen: onTypeModalOpen, onClose: onTypeModalClose } = useDisclosure();

  useEffect(() => {
    fetchTemplates();
    fetchTemplateTypes();
  }, []);

  const fetchTemplates = async () => {
    try {
      setIsLoading(true);
      const response = await fetchWithAuth(
        `${API_URL}/mail-templates`,
        { method: "GET" },
        session
      );
      
      if (response.ok) {
        const fetchedTemplates = await response.json();
        setTemplates(fetchedTemplates);
      }
    } catch (error) {
      console.error("Error fetching mail templates:", error);
      toast.error("Fout bij het ophalen van e-mail sjablonen");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchTemplateTypes = async () => {
    try {
      const response = await fetchWithAuth(
        `${API_URL}/mail-templates/types`,
        { method: "GET" },
        session
      );
      
      if (response.ok) {
        const fetchedTypes = await response.json();
        setTemplateTypes(fetchedTypes);
      }
    } catch (error) {
      console.error("Error fetching template types:", error);
      toast.error("Fout bij het ophalen van sjabloontypen");
    }
  };

  // Template handlers
  const handlePreviewTemplate = (template: EmailTemplate) => {
    setSelectedTemplate(template);
    onPreviewModalOpen();
  };

  const handleEditTemplate = (template: EmailTemplate) => {
    setSelectedTemplate(template);
    onEditModalOpen();
  };

  const handleDeleteTemplate = (template: EmailTemplate) => {
    setSelectedTemplate(template);
    onDeleteModalOpen();
  };

  const handleTemplateSuccess = () => {
    fetchTemplates();
  };

  const handleTypesSuccess = () => {
    fetchTemplateTypes();
  };

  const handleEmailSettingsChange = (settings: EmailSettings | null) => {
    setEmailSettings(settings);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-neutral-primary">E-mail sjablonen</h2>
          <p className="text-sm text-neutral-secondary-mid">
            Beheer je e-mail sjablonen voor verschillende doeleinden
          </p>
        </div>
        <Button
          startContent={<Plus size={16} />}
          className="bg-moss-primary text-white"
          onPress={onCreateModalOpen}
        >
          Nieuw sjabloon
        </Button>
      </div>

      {/* Email Settings Management */}
      <EmailSettingsSection
        onSettingsChange={handleEmailSettingsChange}
      />

      {/* Template Types Management */}
      <TemplateTypesSection
        templateTypes={templateTypes}
        onCreateTypeClick={onTypeModalOpen}
        onTypesChange={handleTypesSuccess}
      />

      {/* Rental Appointment Templates - Read-Only Preview */}
      <div className="bg-blue-50 rounded-lg border border-blue-200 p-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-neutral-primary flex items-center gap-2">
              <span>Rental Appointment Templates</span>
              <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-normal">
                Read-only
              </span>
            </h3>
            <p className="text-sm text-neutral-secondary-mid mt-1">
              Hardcoded email templates for rental appointments - preview only
            </p>
          </div>
          <Button
            onPress={() => navigate('/settings/rental-email-preview')}
            startContent={<Eye size={16} />}
            className="bg-blue-600 text-white"
          >
            Preview Templates
          </Button>
        </div>
      </div>

      {/* Templates Table */}
      <div className="bg-white rounded-lg border border-neutral-primary/10">
        <TemplatesTable
          templates={templates}
          isLoading={isLoading}
          onPreview={handlePreviewTemplate}
          onEdit={handleEditTemplate}
          onDelete={handleDeleteTemplate}
        />
      </div>

      {/* Modals */}
      <CreateTemplateModal
        isOpen={isCreateModalOpen}
        onClose={onCreateModalClose}
        templateTypes={templateTypes}
        emailSettings={emailSettings}
        onSuccess={handleTemplateSuccess}
      />

      <EditTemplateModal
        isOpen={isEditModalOpen}
        onClose={onEditModalClose}
        template={selectedTemplate}
        templateTypes={templateTypes}
        emailSettings={emailSettings}
        onSuccess={handleTemplateSuccess}
      />

      <PreviewTemplateModal
        isOpen={isPreviewModalOpen}
        onClose={onPreviewModalClose}
        template={selectedTemplate}
        emailSettings={emailSettings}
      />

      <DeleteTemplateModal
        isOpen={isDeleteModalOpen}
        onClose={onDeleteModalClose}
        template={selectedTemplate}
        onSuccess={handleTemplateSuccess}
      />

      <CreateTypeModal
        isOpen={isTypeModalOpen}
        onClose={onTypeModalClose}
        onSuccess={handleTypesSuccess}
      />
    </div>
  );
};

export default MailTemplatesTab; 